import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useShallow } from "zustand/shallow";
import { useUIStore } from "../stores/ui-store";
import { UIState } from "../types/ui-types";
import { useTranslation } from "react-i18next";
import { CreateReportingCyclePayload } from "../api/models/reportingCycle.models";
import { createReportingCycle } from "../api/reportingCycle.api";

const useCreateReportingCycle = ({ companyId }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [bearerToken, toast] = useUIStore(
    useShallow((state: UIState) => [state.bearerToken, state.toastRef])
  );

  const createCycle = async (data: CreateReportingCyclePayload) => {
    const result = await createReportingCycleMutation.mutateAsync(data);
    return result;
  };

  const createReportingCycleMutation = useMutation({
    mutationFn: (data: CreateReportingCyclePayload) =>
      // Assuming there's a deleteCompanyUser function in the API
      createReportingCycle(bearerToken as string, { ...data, companyId }),
    onError: (error) => {
      console.error("Failed to create reporting cycle:", error);
      toast?.current?.show({
        severity: "error",
        detail: t("label-reporting-cycle-create-failed"),
        life: 3000,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["reportingCycle", companyId],
      });

      toast?.current?.show({
        severity: "success",
        detail: t("label-reporting-cycle-created"),
        life: 3000,
      });
    },
  });

  const isCreatingReportingCycle = createReportingCycleMutation.isPending;

  return {
    createCycle,
    isCreatingReportingCycle,
  };
};

export default useCreateReportingCycle;
